import {
    PatientDomicileSchema,
    PatientExtended,
    PatientPersonalDataFormValidationSchema,
    PatientPersonalDataSchema,
    PatientResidenceSchema
} from '@/features/patients/types'
import {useTranslation} from '@/translations/i18n'
import {usePatientEditModeView} from '@/features/patients/hooks/usePatientEditModeView'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {PatientDetailsEditButtons} from '../patient-details-edit-buttons/PatientDetailsEditButtons'
import {ObjectKeys} from '@/utilities/helpers'
import {
    getPatientDirtyFieldsFromFormValues,
    remapPatientToDefaultValues,
    remapPatientToPayload
} from '@/features/patients/utils'
import {useUpdatePatient} from '@/features/patients/services/useUpdatePatient'
import {usePatientFormSubmission} from '@/features/patients/hooks/usePatientFormSubmission'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {zodResolver} from '@hookform/resolvers/zod'
import {PatientPersonalInfoDetails} from '@/features/patients/components/patient-personal-info-details/PatientPersonalInfoDetails'
import {PatientPersonalInfoFields} from '@/features/patients/components/patient-personal-info-fields/PatientPersonalInfoFields'
import {useOutletContext} from 'react-router-dom'
import {usePermissions} from '@/features/auth/store/store'
import {z} from 'zod'

const PatientDetailsPersonalInfoSectionSchema = PatientPersonalDataSchema.and(PatientResidenceSchema)
    .and(PatientDomicileSchema)
    .and(
        z.object({
            isDifferentDomicile: z.boolean()
        })
    )
type PatientDetailsPersonalInfoSectionSchema = z.infer<typeof PatientDetailsPersonalInfoSectionSchema>

export const PatientDetailsPersonalInfoSection = () => {
    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const form = useForm<PatientDetailsPersonalInfoSectionSchema>({
        mode: 'onBlur',
        resolver: zodResolver(PatientDetailsPersonalInfoSectionSchema),
        defaultValues: {
            ...remapPatientToDefaultValues(patient),
            isDifferentDomicile: patient.residence?.address != patient.domicile?.address
        }
    })

    return (
        <FormProvider {...form}>
            <PatientDetailsPersonalInfoSectionContent patient={patient} />
        </FormProvider>
    )
}

const PatientDetailsPersonalInfoSectionContent = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = usePatientEditModeView('personal-data')
    const permissions = usePermissions()
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)
    const form = useFormContext<PatientPersonalDataFormValidationSchema>()
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = usePatientFormSubmission({
        formSchema: PatientPersonalDataFormValidationSchema
    })

    // mutation
    const updatePatient = useUpdatePatient({
        urlParams: {patientId: patient.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getPatientDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapPatientToPayload(dirtyFormValues)
            return updatePatient.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('commons:personal_data')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.patients?.write && (
                    <PatientDetailsEditButtons
                        groupName="personal-data"
                        isConfirmDisabled={ObjectKeys(form.formState.dirtyFields).length == 0}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                    />
                )
            }
        >
            {isEditModeEnabled ? (
                <PatientPersonalInfoFields patient={patient} />
            ) : (
                <PatientPersonalInfoDetails patient={patient} />
            )}
        </DetailsFieldsSection>
    )
}
